import { Timezone, getAllTimezones, getTimezone as getCTTimezone } from 'countries-and-timezones';
import { format, zonedTimeToUtc } from 'date-fns-tz';

const NUMBER_OF_MILLISECONDS_IN_A_MINUTE = 60000;

/**
 * @return {string[]} All of the known timezones from 'countries-and-timezones' that the
 * runtime's Intl implementation also accepts (the tz database ships entries like
 * 'Factory' that Intl.DateTimeFormat rejects with a RangeError).
 */
export function getTimezoneNames(): string[] {
  return Object.values(getAllTimezones())
    .map((tz: Timezone) => tz.aliasOf || tz.name)
    .filter((value, i, arr) => arr.indexOf(value) === i)
    .filter(isValidTimezone);
}

/**
 * @param {string} timeZone A candidate IANA timezone name
 * @return {boolean} Whether the runtime's Intl implementation accepts the name, including
 * aliases such as 'UTC', 'Etc/GMT', or 'Asia/Calcutta' that list-membership checks miss.
 */
export function isValidTimezone(timeZone: string): boolean {
  if (!timeZone) {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

const DST_PROBE_DELTAS_MS = [60, 30].map((minutes) => minutes * NUMBER_OF_MILLISECONDS_IN_A_MINUTE);

/**
 * Converts a wall-clock reading in an IANA timezone to the corresponding UTC instant,
 * with moment-timezone parity at DST transitions: nonexistent (spring-forward) times
 * roll forward by the size of the gap, and ambiguous (fall-back) times resolve to the
 * earlier instant. The conversion never round-trips through the host's local timezone.
 *
 * @param {string} wallClock 'yyyy-MM-dd HH:mm[:ss]' or 'yyyy-MM-ddTHH:mm[:ss]'; the time
 * portion may also be 12-hour (e.g. '02:30 PM', including the narrow no-break space that
 * Intl formatters emit before AM/PM on ICU 72+).
 * @param {string} timeZone The IANA timezone the wall clock should be read in.
 * @return {Date} The UTC instant, or an Invalid Date when the input cannot be parsed.
 */
export function wallClockToUtc(wallClock: string, timeZone: string): Date {
  const normalized = normalizeWallClock(wallClock);
  if (!normalized) {
    return new Date(NaN);
  }
  const resolved = zonedTimeToUtc(normalized, timeZone);
  if (isNaN(resolved.getTime())) {
    return resolved;
  }
  const rendersWallClock = (instant: Date) => renderWallClockInZone(instant, timeZone) === normalized;
  if (rendersWallClock(resolved)) {
    for (const delta of DST_PROBE_DELTAS_MS) {
      const earlier = new Date(resolved.getTime() - delta);
      if (rendersWallClock(earlier)) {
        return earlier;
      }
    }
    return resolved;
  }
  const requestedMs = Date.parse(`${normalized}Z`);
  const resolvedRenderingMs = Date.parse(`${renderWallClockInZone(resolved, timeZone)}Z`);
  return new Date(resolved.getTime() + requestedMs - resolvedRenderingMs);
}

/**
 * Renders an instant as a 'yyyy-MM-ddTHH:mm:ss' wall clock in the given zone using
 * Intl directly. date-fns-tz v2's formatInTimeZone is NOT host-independent: it routes
 * the target zone's wall clock through a host-local Date, so wall clocks that land in
 * the HOST zone's DST gap render one hour off. Intl.formatToParts does not.
 */
function renderWallClockInZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`;
}

/**
 * Normalizes a clock-time string to 24-hour 'HH:mm:ss'. Accepts 24-hour ('14:30') and
 * 12-hour ('2:30 PM', '02:30 pm') forms, tolerating the narrow no-break space (U+202F)
 * and thin space (U+2009) that Intl formatters emit before AM/PM on ICU 72+.
 *
 * @param {string} time The clock-time string to normalize
 * @return {string | null} 'HH:mm:ss', or null when the input is not a clock time.
 */
export function normalizeClockTime(time: string): string | null {
  const match = (time || '')
    .replace(/[\u202f\u2009\u00a0]/g, ' ')
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([AaPp])\.?[Mm]\.?)?$/);
  if (!match) {
    return null;
  }
  let hours = Number(match[1]);
  const meridiem = match[4]?.toLowerCase();
  if (meridiem) {
    if (hours < 1 || hours > 12) {
      return null;
    }
    hours = (hours % 12) + (meridiem === 'p' ? 12 : 0);
  }
  if (hours > 23 || Number(match[2]) > 59) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${match[2]}:${match[3] ?? '00'}`;
}

function normalizeWallClock(wallClock: string): string | null {
  const match = (wallClock || '').trim().match(/^(\d{4}-\d{2}-\d{2})[T ](.+)$/);
  if (!match) {
    return null;
  }
  const time = normalizeClockTime(match[2]);
  return time ? `${match[1]}T${time}` : null;
}

/**
 * @param {string} timeZone a timeZone in IANA format
 */
export function getTimezoneObj(timeZone: string): Timezone {
  return getCTTimezone(timeZone) || getCTTimezone('UTC');
}

/**
 * @param {string} timeZone Local timezone of the user  in IANA format, used to get the TimeZone object from 'countries-and-timezones'.
 * @param {Date} fromStart The date that the timezone offset is being fetched for.
 * @return {number} Returns the offset in minutes for the given timezone and date
 */
export function getTimezoneOffsetFromTimezone(timeZone: string, fromStart: Date = new Date()): number {
  const tz: Timezone = getTimezoneObj(timeZone);
  const date = new Date(fromStart.setHours(12));
  if (isTimezoneDST(date, tz)) {
    return tz.dstOffset;
  }
  return tz.utcOffset;
}

/**
 * @param {Date} date The date to check if is during daylight savings time.
 * @return {boolean} Is the given date during daylight savings time
 */
export function isDST(date: Date): boolean {
  const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
  const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) !== date.getTimezoneOffset();
}

/**
 * @param {string} userTimeZone A timezone in the IAN format that the user/client is in.
 * @param {string} timeZone A timezone in IANA format that we want 'fromStart' to be localized to.
 * @param {Date} fromStart A date from the user's timezone to be localized to the supplied timezone
 * @return {string} String for display a timezone option in the timezone selector. Format is '${Timezone abbr} (${timeZone}) - ${current time of "timeZone"}'
 */
export function buildTimezoneDisplay(
  timeZone: string,
  userTimeZone: string = getTimezone(),
  fromStart: Date = new Date(),
): string {
  if (!timeZone) {
    return '';
  }
  let utcDate;
  let zonedDate;
  try {
    utcDate = convertDateToUTCDate(fromStart, userTimeZone);
    zonedDate = convertUTCDateToLocalDate(utcDate, timeZone);
  } catch {
    return '';
  }

  let timeZoneShort = '';
  try {
    // returns a string with the current time and the timezone, we'll split the string to the timezone
    timeZoneShort = zonedDate.toLocaleTimeString(undefined, { timeZone, timeZoneName: 'short' }).split(' ').pop() || '';
  } catch {
    // toLocaleTimeString throws an error when given an unfamiliar timeZone. We'll build our own
    timeZoneShort = buildTimeZoneShortFromTimeZone(timeZone, fromStart);
  }
  let timeDisplay = '';
  try {
    timeDisplay = format(zonedDate, 'h:mm a');
  } catch (err) {
    console.warn('Error formatting time display with', zonedDate, err);
  }
  return `${timeZoneShort} (${timeZone}) - ${timeDisplay}`;
}

/**
 * @param {string} timeZone A timezone, in IANA format
 * @param {Date} date Optional date to determine if DST is in effect
 */
function buildTimeZoneShortFromTimeZone(timeZone: string, date: Date = new Date()): string {
  try {
    const tz = getTimezoneObj(timeZone);

    // Determine if the date is in DST for this timezone
    const isDstForTimezone = isTimezoneDST(date, tz);

    // Use the appropriate offset based on DST status
    const tzOffset: number = isDstForTimezone ? tz.dstOffset : tz.utcOffset;

    const offsetInHours = tzOffset / 60;
    let timeZoneShort = 'GMT';
    if (offsetInHours !== 0) {
      if (offsetInHours > 0) {
        timeZoneShort += '+';
      }
      timeZoneShort += offsetInHours;
      if (tzOffset % 60 !== 0) {
        timeZoneShort += `:${tzOffset % 60}`;
      }
    }
    return timeZoneShort;
  } catch (err) {
    console.warn('Error building short time zone from timezone', timeZone, date, err);
    return '';
  }
}

/**
 * @param {string} timeZone The local timezone of the user/client, in IANA format
 * @param {Date} date A local date to be converted to the current time in UTC
 * @return {Date} The date in UTC
 */
function convertDateToUTCDate(date: Date, timeZone: string): Date {
  const tz = getTimezoneObj(timeZone);

  // Determine if the date is in DST for this timezone
  const isDstForTimezone = isTimezoneDST(date, tz);

  // Use the appropriate offset based on DST status
  const tzOffset = isDstForTimezone ? tz.dstOffset : tz.utcOffset;

  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

  return new Date(now_utc - NUMBER_OF_MILLISECONDS_IN_A_MINUTE * tzOffset);
}

/**
 * Converts a UTC Date to a local Date in the specified IANA timezone.
 *
 * This function calculates the local time by determining whether the input date
 * falls within Daylight Saving Time (DST) for the target timezone. It then applies
 * the correct UTC offset (standard or DST) to the UTC date.
 *
 * Note: This approach avoids relying on browser-local timezone logic and ensures
 * consistent results across environments by using a timezone object with defined
 * offsets.
 *
 * @param {Date} date - A JavaScript Date object in UTC.
 * @param {string} timeZone - The IANA timezone identifier to convert the UTC date to.
 * @return {Date} A new Date object representing the local time in the specified timezone.
 */
function convertUTCDateToLocalDate(date: Date, timeZone: string): Date {
  const tz = getTimezoneObj(timeZone);

  const isDstForTimezone = isTimezoneDST(date, tz);
  const tzOffset = isDstForTimezone ? tz.dstOffset : tz.utcOffset;

  return new Date(date.getTime() + NUMBER_OF_MILLISECONDS_IN_A_MINUTE * tzOffset);
}

/**
 * Determines if a given date is in Daylight Saving Time (DST) for a specific timezone.
 *
 * Zones whose `countries-and-timezones` entry shows no standard/DST offset difference
 * never observe DST. Otherwise the zone's actual UTC offset at the instant is computed
 * via Intl (host-independent) and compared against the entry's DST offset, so callers
 * that pick between `utcOffset`/`dstOffset` end up with the offset that matches reality.
 * Falls back to the browser-local `isDST` check when the instant or zone is invalid.
 *
 * @param {Date} date - The JavaScript Date object to check.
 * @param {Timezone} timezone - A timezone object from the `countries-and-timezones` package.
 * @return {boolean} True if the date is in DST for the given timezone; false otherwise.
 */
function isTimezoneDST(date: Date, timezone: Timezone) {
  if (timezone.utcOffset === timezone.dstOffset) {
    return false;
  }

  try {
    // renderWallClockInZone has seconds precision; drop ms so whole-minute offsets compare exactly
    const offsetMinutes =
      (Date.parse(`${renderWallClockInZone(date, timezone.name)}Z`) - (date.getTime() - date.getMilliseconds())) /
      NUMBER_OF_MILLISECONDS_IN_A_MINUTE;
    if (isNaN(offsetMinutes)) {
      return isDST(date);
    }
    return offsetMinutes === timezone.dstOffset;
  } catch {
    return isDST(date);
  }
}

/**
 * @return {string} Returns the timezone based on client's location, returns UTC when the 'Intl' lib is unavailable, mostly in older browsers.
 */
export function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Intl unavailable assume UTC
    return 'UTC';
  }
}
